import { t } from "@/i18n";
export function getNotAllowedDialogContent(isDbs, panelCategory, panelName = "") {
  let title = `At Home Test Menu - ${isDbs ? 'ON' : 'OFF'}`;
  if (panelCategory === "test") {
    const belongText = isDbs ? 'does not belong' : 'belongs';
    let body = t("OrderPage[\"This ${panelName} cannot be selected because it ${belongText} in the At Home Test Menu.\"]", { panelName, belongText });
    return { title, body };
  }
  let panelCategoryText = panelCategory;
  if (panelCategory === "customPanel") {
    panelCategoryText = t("OrderPage[\"Special Price Custom Panel\"]");
  }
  if (panelCategory === "shortcut") {
    panelCategoryText = t("OrderPage[\"Shortcut Bundle\"]");
  }
  if (panelCategory === "seasonalPromo") {
    panelCategoryText = t("OrderPage[\"Promo\"]");
  }
  const notText = isDbs ? 'not ' : '';
  let body = t("OrderPage[\"This ${panelCategoryText} cannot be selected because it contains tests that are ${notText}in the At Home Test Menu.\"]", { panelCategoryText, notText });
  return { title, body };
}

export function isItemAllowedForCurrentMenu(isDbs, item, itemIsDbsProp = null) {
  let { blood, dryIce, nonBlood } = item.sampleTypeSets;
  if (isDbs) { // when is dbs version
    // if shortcut is dbs version or it doen't contain any blood and dryIce tests
    // then it can be orderd in dbs version, otherwise no
    return item[itemIsDbsProp] || [...blood, ...dryIce].length === 0;
  }
  else {
    // when is serum version
    // disable all dbs version shortcut
    return nonBlood.has("DBS") ? false : true;
  }
}

// check whether item is allow to select according to patient age
// current restriction: 
// saliva & urine hormones are not allowed for patient age < 14
// note that this restriction includes panels, shortcuts & promos that include any of these two tests
// **********************************************************
/* 07/25/2024 update: (VP-6050)  
 * age restriction updates to <= 14
 * If patient DOB was NOT entered, also block them from ordering the tests above.
 * **********************************************************
 * 08/12/2024 update: (VP-6326)
 * age restriction is used for order page only, not for price calculator page
 * **********************************************************
 * 11/15/2024 update: (VP-7574)
 * age restriction updates to < 14
 * add edge case: exactly 14 year old should be restricted (in this case, patient.age will be 13 when calculated in parsePatient file)
 * **********************************************************
 * 11/27/2024 update: (VP-7724)
 * age restriction change back to <= 14 (under 15)
*/
export function isItemAllowedForPatientAge(item, supportObj, patientAge = null) {
  if (patientAge && patientAge > 14) {
    return true;
  }
  let { bundleList } = supportObj;
  let isAllowed = true;
  switch (item.category) {
    case "va":
    case "vw":
      isAllowed = isBelongToHormones(item.id) ? false : true;
      break;
    case "discountPanel":
      isAllowed = discountPanelHasHormones(item) ? false : true;
      break;
    case "customPanel":
      isAllowed = customPanelHasHormones(item, bundleList) ? false : true;
      break;
    case "shortcut": {
      let { discountPanelIdList, customPanelIdList, testIdList } = item;
      let discountPanels = bundleList.filter(b => discountPanelIdList.includes(b.bundleId));
      let customPanels = bundleList.filter(b => customPanelIdList.includes(b.bundleId));
      let testsIncludeHarmones = testIdList.some(id => isBelongToHormones(id));
      let discountPanelsIncludeHarmones = discountPanels.some(panel => discountPanelHasHormones(panel));
      let customPanelsIncludeHarmones = customPanels.some(panel => customPanelHasHormones(panel, bundleList));
      isAllowed = testsIncludeHarmones || discountPanelsIncludeHarmones || customPanelsIncludeHarmones
        ? false : true;
      break;
    }
    case "seasonalPromo": {
      let testIds = item.quantity ? [] : [...item.testIdsInPromo];
      isAllowed = testIds.some(id => isBelongToHormones(id)) ? false : true;
    }
      break;
    default:
      break;
  }
  return isAllowed;
}

// 10/10/24 update: remove age restriction for transmitters(id: 193)
// 10/23/25 update: add age restriction for salivary hormones(id: 841)
// 12/12/25 update: add new hormone zoomer id: 846
function isBelongToHormones(testId) {
  return [59, 194, 195, 835, 846, 841].includes(testId);
}

function discountPanelHasHormones(discountPanel) {
  return discountPanel.testIdsInPanel.some(id => isBelongToHormones(id));
}

function customPanelHasHormones(customPanel, bundleList) {
  let testIds = customPanel.bundleItemList.filter(i => i.itemType !== "official bundle")
    .map(i => i.itemId);
  let discountPanelIds = customPanel.bundleItemList.filter(i => i.itemType === "official bundle" && i.itemId);
  let discountPanels = bundleList.filter(bundle => discountPanelIds.includes(bundle.bundleId));

  return testIds.some(id => isBelongToHormones(id))
    || discountPanels.some(bundle => discountPanelHasHormones(bundle));
}

/**
 * check whether custom panel contains any offline item
 * @param {*} customPanel - custom panel object
 * @param {*} offlineTestIds - list of offline test ids
 * @param {*} offlineDiscountPanelIds - list of offline discount panel ids
 * @returns true if custom panel contains any offline item, otherwise false
 */
export function isCustomPanelContainOfflineItem(customPanel, offlineTestIds = [], offlineDiscountPanelIds = []) {
  let testIdsInCustomPanel = customPanel.bundleItemList.filter(content => content.itemType !== "official bundle")
    .map(item => item.itemId);
  let discountPanelIdsInCustomPanel = customPanel.bundleItemList.filter(content => content.itemType === "official bundle")
    .map(item => item.itemId);
  let containOfflineItem = testIdsInCustomPanel.some(id => offlineTestIds.includes(id))
    || discountPanelIdsInCustomPanel.some(id => offlineDiscountPanelIds.includes(id));
  if (containOfflineItem) {
    return true;
  }
  return false;
}

/**
 * check whether custom panel contains any missing item (item that cannot be found in testList)
 * @param {*} customPanel - custom panel object
 * @param {*} testList - list of available tests
 * @returns true if custom panel contains any missing item, otherwise false
 */
export function isCustomPanelContainMissingItem(customPanel, testList = []) {
  let testIdsInCustomPanel = customPanel.bundleItemList.map(item => item.itemId);

  // Check if any test IDs cannot be found in testList
  return testIdsInCustomPanel.some(testId => {
    return !testList.find(test => test.id === testId || test.originalId === testId);
  });
}
